/**
 * EngineIdentityBadge — a reusable engine icon + connection name + engine name
 * + database chip (`[pg] Analytics PostgreSQL · dvdrental`) so the VQE
 * surface always carries a visible connection/dialect/database cue. The
 * connection name is shown inline because the engine + database alone did not
 * say WHICH server a tab was bound to (a user with two SQL Server connections
 * read "SQL Server · Maintenance" as the database they had open in the
 * explorer). Advisor 2 §B4 (`Ralph/VqePolish/advisors/02-dml-safety-and-polish.md:149-160`):
 * full VQE tabs previously had NO engine/database cue anywhere in the surface,
 * tab titles truncate the engine suffix, and a wrong-database DELETE is exactly
 * the mistake this must prevent (SEL-14, SEL-17, DML-07 identity half).
 *
 * Purely presentational: all data (engine, database, connection name/host) is
 * resolved by the caller and passed in as props, so this component stays a
 * pure reuse point for the compact sticky header.
 *
 * Engine display name reuses the single source of truth,
 * `ENGINE_REGISTRY[engineId].display.shortName` (`core/engines/engineDefinitions.ts`)
 * — the same pattern `dialect/statementCapabilities.ts` already uses — instead
 * of a hand-rolled `mssql → 'SQL Server'` map.
 *
 * The engine is shown with the same icon the Object Explorer uses for the
 * connection (`ConnectionEngineIcon`). It used to be a coloured dot, and a red
 * SQL Server dot next to a red failure banner read as an error light; a dot
 * colour is kept only for the "Not connected" ghost.
 *
 * `variant="compact"` (the compact popover header) drops the engine name — the
 * icon carries it and the header is narrow — and gives the database name
 * priority over the connection name when space runs out; the tooltip then
 * carries the full connection · engine · database line.
 */

import type { ReactNode } from 'react';

import type { DatabaseEngineId } from '../../../../core/engines';
import { ENGINE_REGISTRY } from '../../../../core/engines/engineDefinitions';
import { ConnectionEngineIcon } from '../../../connections/components/ConnectionEngineIcon';
import { VqeTooltip } from '../render/common/VqeTooltip';

// TODO(vqe-copy): consolidate into vqeCopy.ts — locked to a single writer this
// batch (#5); this string mirrors the existing tone of
// `vqeCopy.banner.noConnection.pip` ("Not connected — editing disabled.").
const NOT_CONNECTED_LABEL = 'Not connected';

export interface EngineIdentityBadgeProps {
  /** Resolved engine for the tab's active connection; `undefined` when disconnected. */
  readonly engineId: DatabaseEngineId | undefined;
  /** Active database name, when known. */
  readonly database: string | undefined;
  /** Connection display name (alias) — rendered inline, and in the tooltip. */
  readonly connectionName: string | undefined;
  /** Connection host — rendered inline only when there is no name; always in the tooltip. */
  readonly connectionHost: string | undefined;
  /**
   * Replaces the static database label — e.g. a database picker in the full
   * tab header. Rendered next to (not inside) the tooltip trigger so the two
   * tooltips never nest.
   */
  readonly databaseSlot?: ReactNode;
  /** `compact` — popover header: no engine name, database keeps its width first. Default `full`. */
  readonly variant?: 'full' | 'compact';
  readonly className?: string;
}

/** The inline connection label: the alias, else the host. `Unnamed Connection` is no name. */
function connectionLabel(connectionName: string | undefined, connectionHost: string | undefined): string | undefined {
  if (connectionName && connectionName !== 'Unnamed Connection') return connectionName;
  return connectionHost && connectionHost.length > 0 ? connectionHost : undefined;
}

/** Path-like database values (SQLite files) display as their basename; the
 *  full value stays available for the tooltip. Plain names pass through. */
export function databaseDisplayParts(database: string): { label: string; full: string } {
  const idx = Math.max(database.lastIndexOf('/'), database.lastIndexOf('\\'));
  const label = idx >= 0 ? database.slice(idx + 1) : database;
  return { label: label.length > 0 ? label : database, full: database };
}

/** `["Prod Analytics", "db.internal:5432"]` → `"Prod Analytics · db.internal:5432"`. */
function tooltipText(
  connectionName: string | undefined,
  connectionHost: string | undefined,
  fallback: string,
): string {
  const parts = [connectionName, connectionHost].filter(
    (part): part is string => !!part && part.length > 0,
  );
  return parts.length > 0 ? parts.join(' · ') : fallback;
}

/** Compact tooltip: every part the narrow header may have cut — connection, host, engine, database. */
function compactTooltipText(
  connectionName: string | undefined,
  connectionHost: string | undefined,
  engineName: string,
  database: string | undefined,
): string {
  const parts = [connectionName, connectionHost, engineName, database].filter(
    (part): part is string => !!part && part.length > 0 && part !== 'Unnamed Connection',
  );
  return [...new Set(parts)].join(' · ');
}

export function EngineIdentityBadge({
  engineId,
  database,
  connectionName,
  connectionHost,
  databaseSlot,
  variant = 'full',
  className,
}: EngineIdentityBadgeProps) {
  const baseClass = `inline-flex items-center gap-1.5 text-xs ${className ?? ''}`;

  if (!engineId) {
    return (
      <VqeTooltip selfProvider shrink content={NOT_CONNECTED_LABEL}>
        <span
          data-testid="vqe-engine-identity-badge"
          data-engine="none"
          className={`${baseClass} text-muted-foreground`}
        >
          <span
            data-testid="vqe-engine-identity-badge-dot"
            aria-hidden="true"
            className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40"
          />
          {NOT_CONNECTED_LABEL}
        </span>
      </VqeTooltip>
    );
  }

  const shortName = ENGINE_REGISTRY[engineId].display.shortName;
  const db = database ? databaseDisplayParts(database) : undefined;
  const compact = variant === 'compact';
  const tip = compact
    ? compactTooltipText(connectionName, connectionHost, shortName, db?.full)
    : tooltipText(connectionName, connectionHost, db ? `${shortName} · ${db.full}` : shortName);
  const label = connectionLabel(connectionName, connectionHost);

  const identity = (
    <VqeTooltip selfProvider shrink content={tip}>
      <span
        data-testid="vqe-engine-identity-badge"
        data-engine={engineId}
        data-variant={variant}
        // whitespace-nowrap: under width pressure the database span's TEXT
        // would otherwise wrap at its internal space ("·" staying on the first
        // line, the name dropping below the header baseline); truncate instead.
        className={`${baseClass} min-w-0 whitespace-nowrap text-muted-foreground`}
      >
        <span data-testid="vqe-engine-identity-badge-icon" data-engine={engineId} aria-hidden="true" className="inline-flex shrink-0">
          <ConnectionEngineIcon engine={engineId} size="sm" iconOnly className="h-3.5 w-3.5" />
        </span>
        {label ? (
          <>
            <span data-testid="vqe-engine-identity-badge-connection" className="min-w-0 truncate font-medium text-foreground">
              {label}
            </span>
            {compact ? null : <span className="shrink-0">{shortName}</span>}
          </>
        ) : compact && db ? null : (
          <span className="shrink-0 font-medium text-foreground">{shortName}</span>
        )}
        {db && !databaseSlot ? (
          // Compact: the database keeps its width (up to a cap) and the
          // connection name gives way first; the tooltip has both in full.
          <span
            data-testid="vqe-engine-identity-badge-database"
            className={compact ? 'max-w-[12rem] shrink-0 truncate' : 'truncate'}
          >
            {label || !compact ? `· ${db.label}` : db.label}
          </span>
        ) : null}
      </span>
    </VqeTooltip>
  );
  if (!databaseSlot) return identity;
  return (
    <span className="inline-flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
      {identity}
      <span aria-hidden="true">·</span>
      {databaseSlot}
    </span>
  );
}
